import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import AnalysisCard from './components/AnalysisCard';
import TrendCard from './components/TrendCard';
import AnomalyCard from './components/AnomalyCard';
import RecommendationCard from './components/RecommendationCard';
import InsightChat from './components/InsightChat';
import { analyzeFinancialData } from '../../services/openaiService';
import { getInvoices } from '../../services/invoiceService';
import { getExpenses } from '../../services/expenseService';

const FinancialInsights = () => {
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedAnalysisType, setSelectedAnalysisType] = useState('comprehensive');
  const [financialData, setFinancialData] = useState(null);
  const [loading, setLoading] = useState(true);

  const analysisTypes = [
    { id: 'comprehensive', label: 'Comprehensive Analysis', icon: 'BarChart3', description: 'Complete financial overview' },
    { id: 'trends', label: 'Trend Analysis', icon: 'TrendingUp', description: 'Identify patterns and trends' },
    { id: 'anomalies', label: 'Anomaly Detection', icon: 'AlertTriangle', description: 'Find unusual patterns' },
    { id: 'insights', label: 'Strategic Insights', icon: 'Lightbulb', description: 'Actionable recommendations' },
  ];

  useEffect(() => {
    loadFinancialData();
  }, []);

  const loadFinancialData = async () => {
    setLoading(true);
    try {
      // Fetch all financial data from services
      const [invoices, expenses] = await Promise.all([
        getInvoices(),
        getExpenses()
      ]);

      // Calculate totals
      const totalRevenue = invoices?.reduce((sum, inv) => sum + parseFloat(inv?.paid_amount || 0), 0) || 0;
      const totalExpenses = expenses?.reduce((sum, exp) => sum + parseFloat(exp?.amount || 0), 0) || 0;
      const netProfit = totalRevenue - totalExpenses;
      const cashFlow = netProfit;

      // Format transactions for AI analysis
      const transactions = [
        ...invoices?.map(inv => ({
          date: inv?.issue_date,
          type: 'income',
          amount: parseFloat(inv?.paid_amount || 0),
          category: 'Sales',
          status: inv?.status
        })) || [],
        ...expenses?.map(exp => ({
          date: exp?.date,
          type: 'expense',
          amount: parseFloat(exp?.amount || 0),
          category: exp?.category,
          vendor: exp?.vendor
        })) || []
      ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 10);

      // Group expenses by category
      const expenseBreakdown = {};
      expenses?.forEach(exp => {
        const category = exp?.category || 'Other';
        expenseBreakdown[category] = (expenseBreakdown[category] || 0) + parseFloat(exp?.amount || 0);
      });

      const expenseBreakdownArray = Object.entries(expenseBreakdown)
        .map(([category, amount]) => ({ category, amount }))
        .sort((a, b) => b.amount - a.amount);

      // Group revenue by month (simplified)
      const revenueByMonth = {};
      invoices?.forEach(inv => {
        if (inv?.paid_amount && inv?.issue_date) {
          const month = new Date(inv.issue_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
          revenueByMonth[month] = (revenueByMonth[month] || 0) + parseFloat(inv.paid_amount);
        }
      });

      const revenueSources = Object.entries(revenueByMonth)
        .map(([source, amount]) => ({ source, amount }))
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 3);

      const data = {
        cashFlow: `$${cashFlow?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        revenue: `$${totalRevenue?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        expenses: `$${totalExpenses?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        netProfit: `$${netProfit?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        period: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        transactions,
        expenseBreakdown: expenseBreakdownArray,
        revenueSources: revenueSources?.length > 0 ? revenueSources : [{ source: 'No revenue data', amount: 0 }]
      };

      setFinancialData(data);
    } catch (error) {
      console.error('Error loading financial data:', error);
      setError('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!financialData) {
      setError('No financial data available to analyze');
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    
    try {
      const result = await analyzeFinancialData(financialData, selectedAnalysisType);
      setAnalysisResult(result);
    } catch (err) {
      setError(err?.message || 'Failed to analyze financial data. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'muted';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-[1920px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-2">
                  Financial Insights
                </h1>
                <p className="font-body text-base md:text-lg text-muted-foreground">
                  AI-powered analysis of your accounting data
                </p>
              </div>
              <Button
                onClick={handleAnalyze}
                loading={isAnalyzing}
                iconName="Sparkles"
                size="lg"
                disabled={isAnalyzing}
              >
                {isAnalyzing ? 'Analyzing...' : 'Generate Analysis'}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 md:mb-8">
            {analysisTypes?.map((type) => (
              <button
                key={type?.id}
                onClick={() => setSelectedAnalysisType(type?.id)}
                className={`
                  p-4 rounded-lg border-2 transition-smooth hover-lift text-left
                  ${selectedAnalysisType === type?.id
                    ? 'border-primary bg-primary/5' :'border-border bg-card hover:border-primary/50'
                  }
                `}
              >
                <div className="flex items-start gap-3">
                  <div className={`
                    w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0
                    ${selectedAnalysisType === type?.id ? 'bg-primary/10' : 'bg-muted'}
                  `}>
                    <Icon 
                      name={type?.icon} 
                      size={20} 
                      color={selectedAnalysisType === type?.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-body font-semibold text-sm text-foreground mb-1">
                      {type?.label}
                    </h3>
                    <p className="caption text-muted-foreground">
                      {type?.description}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {error && (
            <div className="mb-6 p-4 bg-error/10 border border-error rounded-lg flex items-start gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-error)" />
              <div className="flex-1">
                <p className="font-body font-medium text-sm text-error mb-1">Analysis Failed</p>
                <p className="caption text-error/80">{error}</p>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
                <p className="mt-4 text-muted-foreground">Loading financial data...</p>
              </div>
            </div>
          ) : (
            <>
              {analysisResult && (
                <>
                  <div className="mb-6 md:mb-8">
                    <AnalysisCard 
                      summary={analysisResult?.summary}
                      confidence={analysisResult?.confidence}
                      keyFindings={analysisResult?.keyFindings}
                      riskAssessment={analysisResult?.riskAssessment}
                      getRiskColor={getRiskColor}
                    />
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 md:mb-8">
                    <div className="space-y-6">
                      <div className="bg-card rounded-lg shadow-sm border border-border p-4 md:p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-primary/10 rounded-md flex items-center justify-center">
                            <Icon name="TrendingUp" size={20} color="var(--color-primary)" />
                          </div>
                          <h2 className="font-heading font-semibold text-xl text-foreground">
                            Trends Identified
                          </h2>
                        </div>
                        <div className="space-y-3">
                          {analysisResult?.trends?.map((trend, index) => (
                            <TrendCard key={index} trend={trend} />
                          ))}
                        </div>
                      </div>

                      <div className="bg-card rounded-lg shadow-sm border border-border p-4 md:p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-warning/10 rounded-md flex items-center justify-center">
                            <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
                          </div>
                          <h2 className="font-heading font-semibold text-xl text-foreground">
                            Anomalies Detected
                          </h2>
                        </div>
                        <div className="space-y-3">
                          {analysisResult?.anomalies?.length > 0 ? (
                            analysisResult?.anomalies?.map((anomaly, index) => (
                              <AnomalyCard key={index} anomaly={anomaly} />
                            ))
                          ) : (
                            <p className="caption text-muted-foreground">No significant anomalies detected</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div className="bg-card rounded-lg shadow-sm border border-border p-4 md:p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-success/10 rounded-md flex items-center justify-center">
                            <Icon name="Lightbulb" size={20} color="var(--color-success)" />
                          </div>
                          <h2 className="font-heading font-semibold text-xl text-foreground">
                            Recommendations
                          </h2>
                        </div>
                        <div className="space-y-3">
                          {analysisResult?.recommendations?.map((recommendation, index) => (
                            <RecommendationCard key={index} recommendation={recommendation} />
                          ))}
                        </div>
                      </div>

                      <InsightChat financialData={financialData} />
                    </div>
                  </div>
                </>
              )}
              {!analysisResult && !isAnalyzing && (
                <div className="bg-card rounded-lg shadow-sm border border-border p-8 md:p-12 text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Sparkles" size={32} color="var(--color-primary)" />
                  </div>
                  <h3 className="font-heading font-semibold text-xl text-foreground mb-2">
                    Ready to Analyze Your Financial Data
                  </h3>
                  <p className="font-body text-base text-muted-foreground mb-6">
                    {financialData ? 'Select an analysis type and click "Generate Analysis" to get AI-powered insights' : 'No financial data available. Add invoices and expenses to get started.'}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
                    <div className="p-4 bg-muted/50 rounded-lg text-left">
                      <Icon name="TrendingUp" size={20} color="var(--color-primary)" className="mb-2" />
                      <p className="font-body font-medium text-sm text-foreground mb-1">Trend Analysis</p>
                      <p className="caption text-muted-foreground">Identify revenue and expense patterns</p>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-lg text-left">
                      <Icon name="AlertTriangle" size={20} color="var(--color-warning)" className="mb-2" />
                      <p className="font-body font-medium text-sm text-foreground mb-1">Anomaly Detection</p>
                      <p className="caption text-muted-foreground">Find unusual transactions and outliers</p>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-lg text-left">
                      <Icon name="Lightbulb" size={20} color="var(--color-success)" className="mb-2" />
                      <p className="font-body font-medium text-sm text-foreground mb-1">Strategic Insights</p>
                      <p className="caption text-muted-foreground">Get actionable business recommendations</p>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-lg text-left">
                      <Icon name="Shield" size={20} color="var(--color-accent)" className="mb-2" />
                      <p className="font-body font-medium text-sm text-foreground mb-1">Risk Assessment</p>
                      <p className="caption text-muted-foreground">Evaluate financial health and risks</p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <QuickActionToolbar />
    </div>
  );
};

export default FinancialInsights;
